import RenderNode from "./components/RenderNode";
import Joyride, { STATUS } from "react-joyride";
import { Editor, useEditor } from "@craftjs/core";
import { blockResolvers } from "./../blocks";
import GrigoraEditorHead from "./grigora-editor-head";
import GrigoraEditorLeftBar from "./grigora-left-bar";
import GrigoraEditorMain from "./grigora-editor-main";
import GrigoraEditorRightBar from "./grigora-right-bar";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { GrigoraEditorContext, GrigoraEditorProvider } from "./context";
import ProjectLoader from "../loader/project";
import { Helmet } from "react-helmet-async";
import { GrigoraDashboardContext } from "../dashboard/context";
import { debounce, set } from "lodash";
import { GrigoraFormatsProvider } from "../formats";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import IframeResizer from "iframe-resizer-react";
import KeyboardModal from "../keyboard-shortcuts";
import { IS_MAC, isMac } from "../utils";
import blockActions from "../block-actions";
import { clearSelection, isFocusedInput, isFocusedInputDocument } from "../dom";
import EditorSkeleton from "../skeletons/editor-skeleton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTabletAlt } from "@fortawesome/free-solid-svg-icons";
import { WhiteLabelContext } from "../whitelabel";
import { APP_NAME } from "../app";

// Change the structure to
const KEYBOARD_SHORTCUTS = {
  "common-actions": {
    label: "Common Actions",
    shortcuts: [
      {
        id: "copy",
        label: "Copy",
        windows: "Ctrl + C",
        mac: "⌘ + C",
      },
      {
        id: "cut",
        label: "Cut",
        windows: "Ctrl + X",
        mac: "⌘ + X",
      },
      {
        id: "paste",
        label: "Paste",
        windows: "Ctrl + V",
        mac: "⌘ + V",
      },
      {
        id: "undo",
        label: "Undo",
        windows: "Ctrl + Z",
        mac: "⌘ + Z",
      },
      {
        id: "redo",
        label: "Redo",
        windows: "Ctrl + Shift + Z",
        mac: "⌘ + Shift + Z",
      },
      {
        id: "show-keyboard-shortcuts",
        label: "Show Keyboard Shortcuts",
        windows: "Ctrl + /",
        mac: "⌘ + /",
      },
      {
        id: "save",
        label: "Save",
        windows: "Ctrl + S",
        mac: "⌘ + S",
      },
      {
        id: "add-new-elements",
        label: "Add Elements",
        windows: "Ctrl + Enter",
        mac: "⌘ + Enter",
      },
    ],
  },
  "block-actions": {
    label: "Block Actions",
    shortcuts: [
      {
        id: "open-close-settings",
        label: "Open/Close Settings",
        windows: "Ctrl + E",
        mac: "⌘ + E",
      },

      {
        id: "block-type",
        label: "Block Suggestions",
        windows: "/ type",
        mac: "/ type",
      },
      {
        id: "duplicate-block",
        label: "Duplicate Block",
        windows: "Ctrl + D",
        mac: "⌘ + D",
      },
      {
        id: "delete-block",
        label: "Delete Block",
        windows: "Backspace/Delete",
        mac: "Delete",
      },
      {
        id: "group",
        label: "Wrap in Container",
        windows: "Ctrl + G",
        mac: "⌘ + G",
      },
      {
        id: "ungroup",
        label: "Unwrap Container",
        windows: "Ctrl + Shift + G",
        mac: "⌘ + Shift + G",
      },
      {
        id: "insert-inside-before",
        label: "Insert Before",
        windows: "Ctrl + Shift + Left",
        mac: "⌘ + Shift + Left",
      },
      {
        id: "insert-inside-after",
        label: "Insert Before",
        windows: "Ctrl + Shift + Right",
        mac: "⌘ + Shift + Right",
      },
      {
        id: "insert-before",
        label: "Insert Before",
        windows: "Ctrl + Shift + Up",
        mac: "⌘ + Shift + Up",
      },
      {
        id: "insert-after",
        label: "Insert After",
        windows: "Ctrl + Shift + Down",
        mac: "⌘ + Shift + Down",
      },
      {
        id: "select-parent",
        label: "Select Parent",
        windows: "Up",
        mac: "Up",
      },
      {
        id: "select-children",
        label: "Select Children",
        windows: "Down",
        mac: "Down",
      },
      // Select Previous Sibling Element
      {
        id: "select-previous-sibling",
        label: "Select Previous Sibling",
        windows: "Left",
        mac: "Left",
      },
      {
        id: "select-next-sibling",
        label: "Select Next Sibling",
        windows: "Right",
        mac: "Right",
      },
      // Move Before Previous Sibling Element
      {
        id: "move-before-previous-sibling",
        label: "Move Before Previous Sibling",
        windows: "Alt + Left",
        mac: "Option + Left",
      },
      {
        id: "move-after-next-sibling",
        label: "Move After Next Sibling",
        windows: "Alt + Right",
        mac: "Option + Right",
      },
    ],
  },
};

const tourSteps = [
  {
    title: "🎉 Welcome to Editor!",
    target: "body",

    content: (
      <div className="text-sm">
        <p>
          With our user-friendly interface, you can easily customize your
          website and make them look professional in no time. 💁‍♀️
        </p>
        <p className="mt-2">Let&apos;s take a quick tour to get you started.</p>
      </div>
    ),
    placement: "center",
    disableBeacon: true,
  },

  {
    title: "Site Settings",
    target: "#site-logo",
    content: (
      <div className="text-sm">
        <p>
          It has options to Save, Exit, Keyboard Shortcuts, and open settings
          related to this website.
        </p>
      </div>
    ),
    placement: "auto",
    disableBeacon: true,
  },
  {
    title: "Keyboard Shortcuts",
    target: "#keyboardShortcuts",
    content: (
      <div className="text-sm">
        <p>
          Keyboard shortcut will be open by pressing{" "}
          <strong>{isMac() ? "⌘ + /" : "Ctrl + /"}</strong> key
        </p>
      </div>
    ),
    placement: "auto",
    disableBeacon: true,
  },
  {
    title: "Add Elements",
    target: "#add-elements",
    content: (
      <div className="text-sm">
        <p>
          Add blocks specially build for your websites.
          <br></br>
          <br></br>
          Good thing is that you can customize every block as you want.
        </p>
      </div>
    ),
    placement: "auto",
    disableBeacon: true,
  },
  {
    title: "Components",
    target: "#components",
    content: (
      <div className="text-sm">
        <p>
          Add your custom componenents and use it anywhere 🔥 in your website.
        </p>
      </div>
    ),
    placement: "auto",
    disableBeacon: true,
  },
  {
    title: "Pages",
    target: "#pages",
    content: (
      <div className="text-sm">
        <p>
          Create multiple pages 💻 for you website and link them for better
          utilization of you content.
        </p>
      </div>
    ),
    placement: "auto",
    disableBeacon: true,
  },
  {
    title: "Typography",
    target: "#typography",
    content: (
      <div className="text-sm">
        <p>
          Each and every font is 🖊 customizable. We have an option to add your
          custom ✨ typography.
        </p>
      </div>
    ),
    placement: "auto",
    disableBeacon: true,
  },
  {
    title: "Settings",
    target: "#settings",
    content: (
      <div className="text-sm">
        <p>
          There are several options to customize your theme colors, icons &
          fonts. You can also change the name of website here.
        </p>
      </div>
    ),
    placement: "right",
    disableBeacon: true,
  },
  {
    title: "Layers",
    target: "#layers",
    content: (
      <div className="text-sm">
        <p>A blueprint showing the structure of your website.</p>
      </div>
    ),
    placement: "top",
    disableBeacon: true,
  },
  {
    title: "Support",
    target: "#support",
    content: (
      <div className="text-sm">
        <p>If you need help, or face any issue, you can contact us here.</p>
      </div>
    ),
    placement: "top",
    disableBeacon: true,
  },
  {
    title: "Devices",
    target: "#devices",
    content: (
      <div className="text-sm">
        <p>
          See how your website looks in 🖥 Desktop,{" "}
          <FontAwesomeIcon icon={faTabletAlt} /> Tablet 📱 and Mobile.
        </p>
      </div>
    ),
    placement: "auto",
    disableBeacon: true,
  },
  {
    title: "Undo Redo",
    target: "#undo-redo",
    content: (
      <div className="text-sm">
        <p>
          If you done something wrong 😯 by mistake, then don&apos;t worry we
          have an options to undo & redo things.
        </p>
      </div>
    ),
    placement: "auto",
    disableBeacon: true,
  },
  {
    title: "Website Name",
    target: "#project-name",
    content: (
      <div className="text-sm">
        <p>The name of your website. Your can also edit it.</p>
      </div>
    ),
    placement: "auto",
    disableBeacon: true,
  },
  // {
  //   title: "Version",
  //   target: "#version",
  //   content: (
  //     <div className="text-sm">
  //       <p>Obviously, the latest version 🏆 of Grigora</p>
  //     </div>
  //   ),
  //   placement: "top",
  //   disableBeacon: true,
  // },
  // {
  //   title: "Debug",
  //   target: "#debug",
  //   content: (
  //     <div className="text-sm">
  //       <p>Developer Tools available for deep customization of your website.</p>
  //     </div>
  //   ),
  //   placement: "auto",
  //   disableBeacon: true,
  // },
  {
    title: "Canvas",
    target: "#page-container",
    content: (
      <div className="text-sm">
        <p>The main section where you can create and edit your website.</p>
      </div>
    ),
    placement: "auto",
    disableBeacon: true,
  },
  {
    title: "Settings Panel",
    target: "#settings-panel",
    content: (
      <div className="text-sm">
        <p>
          List of settings available for the selected block. You can press{" "}
          <strong>{isMac() ? "⌘ + E" : "Ctrl + E"}</strong> to open the
          respective settings and customize according to your need.
        </p>
      </div>
    ),
    placement: "auto",
    disableBeacon: true,
  },
  {
    title: "Publish",
    target: "#publish",
    content: (
      <div className="text-sm">
        <p>
          When you done with all 🎉 the changes publish your website. You can
          save it as draft as well.
        </p>
      </div>
    ),
    placement: "auto",
    disableBeacon: true,
  },
];

function isFocusedOnRichText(document) {
  const activeElement = document.getSelection()?.focusNode;
  const contentEditable = activeElement?.parentElement?.closest(
    ".grigora-richtext-input__field[contenteditable='true']",
  );

  return contentEditable;
}

function isFocusedOnRichTextInsideBlock(document, id) {
  const contentEditable = isFocusedOnRichText(document);

  if (!contentEditable) {
    return false;
  }

  const block = contentEditable?.closest(`[data-grigora-block-id="${id}"]`);
  return block;
}

const EditorRaw = () => {
  const [run, setRun] = useState(
    localStorage.getItem("grigora-editor-tour") == "true" ||
      !localStorage.getItem("grigora-editor-tour")
      ? true
      : false,
    // true,
  );

  const [isJoyrideOpen, setIsJoyrideOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const { setScreen, activeScreen, projectID, editorDocument } = useContext(
    GrigoraDashboardContext,
  );
  const {
    isLoading,
    currentPage,
    pageContents,
    setPagesContents,
    project,
    projectComponents,
    setProjectComponents,
    updatePages,
    keyboardShortcutModal,
    setKeyboardShortcutModal,
    isBlockSettingsVisible,
    setIsBlockSettingsVisible,
    setLeftWidget,
    setIsAddingBlocks,
    setIsAddingSections,
  } = useContext(GrigoraEditorContext);

  const { query, actions, id } = useEditor((state, query) => ({
    enabled: state.options.enabled,
    state: state,
    id: query.getEvent("selected").first(),
  }));

  const toggleBlockSettings = useCallback(() => {
    setIsBlockSettingsVisible(!isBlockSettingsVisible);
  }, [isBlockSettingsVisible, setIsBlockSettingsVisible]);

  const editorOnKeyDown = useCallback(
    (e) => {
      const id = query.getEvent("selected").first();

      const ctrlKey = isMac() ? e.metaKey : e.ctrlKey;

      if (ctrlKey) {
        if (e.keyCode === 68) {
          // Duplicate Blocks
          blockActions.duplicate(actions, query);
          e.preventDefault();
          return;
        } else if (e.shiftKey && e.keyCode === 71) {
          // Unwrap block
          blockActions.unwrap(actions, query, null, editorDocument);
          e.preventDefault();
          return;
        } else if (!e.shiftKey && e.keyCode === 71) {
          // Wrap block

          // don't wrap slides of slider block
          const parentNodeId = query?.node(id)?.get()?.data?.parent;
          const selectedNodeSlug = query.node(parentNodeId)?.get()?.data
            ?.custom?.slug;

          if (selectedNodeSlug === "grigora/slider") {
            e.preventDefault();
            return;
          }

          blockActions.wrap(
            actions,
            query,
            "grigora/container",
            editorDocument,
          );
          e.preventDefault();
          return;
        }
      }

      if (
        (!isFocusedOnRichTextInsideBlock(editorDocument, id) &&
          !isFocusedInputDocument(editorDocument) &&
          (e.keyCode === 8 || e.keyCode === 46)) ||
        ((e.keyCode === 8 || e.keyCode === 46) && ctrlKey)
      ) {
        // Delete
        blockActions.delete(actions, query);
        e.preventDefault();
        return;
      }

      if (ctrlKey && e.shiftKey && e.keyCode === 37) {
        // Insert Inside Parent In Index 0
        blockActions.insertInside(
          actions,
          query,
          null,
          "grigora/text",
          true,
          editorDocument,
        );
        e.preventDefault();
        return;
      } else if (ctrlKey && e.shiftKey && e.keyCode === 39) {
        // Insert Inside Parent In Last Index
        blockActions.insertInside(
          actions,
          query,
          null,
          "grigora/text",
          false,
          editorDocument,
        );
        e.preventDefault();
        return;
      } else if (ctrlKey && e.shiftKey && e.keyCode === 38) {
        // Insert Before
        blockActions.insert(
          actions,
          query,
          null,
          "grigora/text",
          true,
          editorDocument,
        );
        e.preventDefault();
        return;
      } else if (ctrlKey && e.shiftKey && e.keyCode === 40) {
        // Insert After
        blockActions.insert(
          actions,
          query,
          null,
          "grigora/text",
          false,
          editorDocument,
        );
        e.preventDefault();
        return;
      }

      if (!isFocusedOnRichTextInsideBlock(editorDocument, id) || ctrlKey) {
        if (e.keyCode === 38) {
          // Select Parent
          blockActions.selectParent(actions, query);
          clearSelection(editorDocument);
          e.preventDefault();
          return;
        } else if (e.keyCode === 40) {
          // Select Child
          blockActions.selectChild(actions, query);
          clearSelection(editorDocument);
          e.preventDefault();
          return;
        }
        if (!e.altKey && e.keyCode === 37) {
          // Select Previous Sibling
          blockActions.selectPreviousSibling(actions, query);
          clearSelection(editorDocument);
          e.preventDefault();
          return;
        } else if (!e.altKey && e.keyCode === 39) {
          // Select Next Sibling
          blockActions.selectNextSibling(actions, query);
          clearSelection(editorDocument);
          e.preventDefault();
          return;
        }
      }

      if (e.altKey) {
        if (e.keyCode === 37) {
          // Move Left
          blockActions.moveLeft(actions, query);
          e.preventDefault();
          return;
        } else if (e.keyCode === 39) {
          // Move Right
          blockActions.moveRight(actions, query);
          e.preventDefault();
          return;
        }
      }
    },
    [editorDocument, isFocusedOnRichTextInsideBlock, isFocusedOnRichText],
  );

  const handleOnKeyDown = useCallback(
    (e) => {
      const id = query.getEvent("selected").first();

      const ctrlKey = isMac() ? e.metaKey : e.ctrlKey;

      if (ctrlKey) {
        if (
          (IS_MAC && !e.shiftKey && e.keyCode === 90) ||
          (!IS_MAC && e.keyCode === 90)
        ) {
          // Undo
          if (query.history.canUndo()) {
            actions.history.undo();
            // editorDocument.querySelector(".grigoraEditor")?.focus();
          }
          e.preventDefault();
          return;
        } else if (
          (IS_MAC && e.shiftKey && e.keyCode === 90) ||
          (!IS_MAC && e.keyCode === 89)
        ) {
          // Redo
          if (query.history.canRedo()) {
            actions.history.redo();
            // editorDocument.querySelector(".grigoraEditor")?.focus();
          }
          e.preventDefault();
          return;
        } else if (e.keyCode === 83) {
          // Save
          updatePages(query);
          e.preventDefault();
          return;
        } else if (e.keyCode === 191) {
          // Keyboard shortcuts
          setKeyboardShortcutModal(true);
        }
        // Block Settings
        if (ctrlKey && e.keyCode === 69) {
          toggleBlockSettings();
          e.preventDefault();
        }
        // Open Add Elements. Ctrl + enter
        if (ctrlKey && e.keyCode === 13) {
          setLeftWidget("");
          setIsAddingBlocks(true);
          e.preventDefault();
        }
      }

      // Return if focused on input
      if (isFocusedInput(document)) {
        return;
      }

      editorOnKeyDown(e);

      if (e.altKey) {
        if (e.keyCode === 37) {
          // Prevent Accidental Browser Window Change - Move Left
          e.preventDefault();
          return;
        } else if (e.keyCode === 39) {
          // Prevent Accidental Browser Window Change - Move Right
          e.preventDefault();
          return;
        }
      }
    },
    [
      isBlockSettingsVisible,
      toggleBlockSettings,
      updatePages,
      editorDocument,
      editorOnKeyDown,
    ],
  );

  // Check if some events are missing and fix the below code
  // useEffect(() => {
  //   editorDocument?.addEventListener("keydown", editorOnKeyDown);

  //   return () => {
  //     editorDocument?.removeEventListener("keydown", editorOnKeyDown);
  //   };
  // }, [editorDocument, editorOnKeyDown]);

  useEffect(() => {
    document?.addEventListener("keydown", handleOnKeyDown);
    editorDocument?.addEventListener("keydown", handleOnKeyDown);

    return () => {
      document?.removeEventListener("keydown", handleOnKeyDown);
      editorDocument?.removeEventListener("keydown", handleOnKeyDown);
    };
  }, [handleOnKeyDown, document, editorDocument]);

  return (
    <>
      <style>
        {`:root{
          ${
            project.colors &&
            Array.isArray(project.colors) &&
            project.colors
              .map((item) => {
                return `--gb--${item.title}: ${item.color};`;
              })
              .join("")
          }
        }`}
      </style>
      <Joyride
        run={run}
        spotlightPadding={2}
        callback={({ status, step }) => {
          if (step.target === "#page-container") {
            setIsBlockSettingsVisible(true);
          }
          if ([STATUS.FINISHED].includes(status)) {
            setIsBlockSettingsVisible(false);
          }

          if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
            window.localStorage.setItem("grigora-editor-tour", "false");
            setRun(false);
          }
        }}
        continuous
        steps={tourSteps}
        showSkipButton
        disableOverlayClose={false}
        disableScrollParentFix={true}
        locale={{
          last: "Finish",
        }}
        styles={{
          options: {
            primaryColor: "#5cbd20",
            zIndex: 999999,
          },
          content: {
            fontSize: "14px",
          },
          buttonSkip: {
            backgroundColor: "#ffffff",
            color: "black",
            padding: "8px 16px",
            borderRadius: "21px",
            border: "none",
            fontSize: "14px",
            boxShadow: "none",
          },
          buttonBack: {
            backgroundColor: "#ffffff",
            color: "black",
            padding: "8px 16px",
            borderRadius: "21px",
            border: "none",
            fontSize: "14px",
            boxShadow: "none",
          },
          buttonNext: {
            backgroundColor: "rgb(92, 189, 32)",
            color: "#fff",
            padding: "8px 16px",
            borderRadius: "21px",
            border: "none",
            fontSize: "14px",
            boxShadow: "none",
            outline: "none",
          },
        }}
      />
      <KeyboardModal
        open={keyboardShortcutModal}
        setOpen={setKeyboardShortcutModal}
        keyboardShortcuts={KEYBOARD_SHORTCUTS}
      />
      <GrigoraEditorHead setRunEditorTutorial={setRun} />
      <div className="flex flex-1 overflow-hidden">
        <GrigoraEditorLeftBar />
        <motion.div
          id="page-container"
          className="page-container overflow-hidden flex flex-1 relative bg-[rgb(239,240,243)]"
        >
          <GrigoraEditorMain />
        </motion.div>
        <GrigoraEditorRightBar isJoyrideOpen={run} />
      </div>
    </>
  );
};

function GrigoraEditorWrapper({ children, ...props }) {
  const [isMobile, setIsMobile] = useState(false);
  const { setScreen, activeScreen, projectID, editorDocument } =
    useContext(GrigoraDashboardContext) ?? {};
  const {
    isLoading,
    currentPage,
    pageContents,
    setPagesContents,
    project,
    projectComponents,
    setProjectComponents,
  } = useContext(GrigoraEditorContext);

  const { __whitelabel__ } = useContext(WhiteLabelContext) ?? {};

  // Stop the invocation of the debounced function
  // after unmounting
  useEffect(() => {
    return () => {
      debouncedChangeHandler.cancel();
    };
  }, []);

  const handleScreenSize = () => {
    if (window.innerWidth < 640) setIsMobile(true);
    else setIsMobile(false);
  };

  useEffect(() => {
    setScreen?.("site-editor");
    if (window.innerWidth < 768) setIsMobile(true);
    else setIsMobile(false);
    window.addEventListener("resize", handleScreenSize);
  }, []);

  // Option B: useMemo() stores the debounced callback
  const debouncedChangeHandler = useMemo(
    () =>
      debounce((query) => {
        if (currentPage && currentPage.startsWith("component-")) {
          const json = query.serialize();
          var pageData = JSON.parse(json);
          var rootNode = pageData["ROOT"]?.nodes && pageData["ROOT"]?.nodes[0];

          // delete root node
          delete pageData["ROOT"];

          setProjectComponents({
            ...projectComponents,
            [currentPage]: {
              ...(projectComponents[currentPage]
                ? projectComponents[currentPage]
                : {}),
              root_node: rootNode,
              content: JSON.stringify({
                rootNodeId: rootNode,
                nodes: JSON.stringify(pageData),
              }),
            },
          });
        } else {
          const json = query.serialize();
          setPagesContents({ ...pageContents, [currentPage]: json });
        }
      }, 300),
    [pageContents, currentPage],
  );

  if (isLoading) {
    if (activeScreen === "editor")
      return <EditorSkeleton key={"editor-skeleton"} />;
    else return;
  }

  if (isMobile) {
    return (
      <div className="h-screen bg-white flex item-center justify-center">
        <div className="bg-white p-5 rounded-[21px] mt-[30%] h-fit">
          <h1>Editor is not supported in mobile.</h1>
          <div className="flex gap-4 mt-5">
            <Link to={`/site/${projectID}`}>
              <button className="border-2 rounded-[21px] bg-white text-sm p-2 px-5">
                Exit
              </button>
            </Link>
            <button className="border-2 border-[#5cbd20] bg-[#5cbd20] text-white rounded-[21px] text-sm p-2 px-5">
              Publish
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>
          {project.name} - Site Editor -{" "}
          {__whitelabel__("platformName", APP_NAME)}
        </title>
      </Helmet>
      <div className="grigoraEditor flex w-[100vw] h-[100vh] flex-col overflow-hidden">
        <Editor
          key={currentPage}
          resolver={blockResolvers}
          onRender={RenderNode}
          onNodesChange={debouncedChangeHandler}
          indicator={{
            thickness: 4,
            success: "rgb(37, 99, 235)",
            error: "red",
            transition: "0.2s ease-in",
          }}
        >
          {children}
        </Editor>
      </div>
    </>
  );
}

function GrigoraEditor(props) {
  const { __whitelabel__ } = useContext(WhiteLabelContext) ?? {};
  const { project } = useContext(GrigoraDashboardContext) ?? {};
  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Editor - {__whitelabel__("platformName", APP_NAME)}</title>
      </Helmet>
      <GrigoraEditorProvider>
        <GrigoraEditorWrapper>
          <GrigoraFormatsProvider>
            <EditorRaw />
          </GrigoraFormatsProvider>
        </GrigoraEditorWrapper>
      </GrigoraEditorProvider>
    </>
  );
}

export default GrigoraEditor;
